import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import secureAxios from '../../core/api/client/secureAxios';
import { Collapse } from '@material-ui/core';
import DropdownButton from 'react-bootstrap/lib/DropdownButton';
import MenuItem from 'react-bootstrap/lib/MenuItem';
import fileSaver from 'file-saver';
import moment from 'moment';

import ImportFromFile from '../../components/Controls/ImportFromFile';
import ConfirmationMessage from '../Controls/ConfirmationMessage';
import PoliciesSection from './PoliciesSection';
import PoliciesSettingsModal from './PoliciesSettingsModal';
import { downloadFile, httpGet, httpPost } from '../utils';
import { getPoliciesListByAction, sortPolicies } from './PoliciesUtils';

import {
    DEFAULT_POLICY_ID,
    FEATURE_FLAGS_NAMES,
    HTTP_OPTIONS,
    POLICY_ACTION_TYPES,
    POLICY_MODE_TYPES,
    UNPAIRED_USERS_ACTION_OPTIONS
} from '@silverfort-nodelibs/common/utils/consts';

import styles from './CssModules/Policies.module.css';
import navBarStyles from '../common/css/NavBar.module.css';
import whitePoliciesSelectedIcon from '../common/Images/policies_selected_icon.png';
import FiltersMenu from '../Controls/Filters/FiltersMenu';
import { adEntitiesAndLocalAccounts, POLICIES_FILTERS, resources } from '../Controls/Filters/FiltersObjects';
import { EXTERNAL } from '../Controls/Filters/filterTypes';
import useFeature from '../SystemSettings/hooks/featureFlags/useFeature';
import { SYSTEM_MODES } from '@silverfort-nodelibs/common/utils/system-mode-config';
import { isEmptyObject } from '@silverfort-nodelibs/common/utils/common';
import { useTowerLayout } from '../../TowerLayout/TowerLayoutProvider';
import { Button, Icon, Icons } from '@silverfort/sf-components';

const UPDATE_DATE_FORMAT = 'YYYY-MM-DDThh:mm:ss.000000';
const Policies = ({ isEditor }) => {
    const location = useLocation();
    const history = useHistory();
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const templateToEdit = searchParams.get('templateToEdit');

    const [showLoader, setShowLoader] = useState(true);
    const systemMode = useFeature(FEATURE_FLAGS_NAMES.SystemMode);
    const [policiesCount, setPoliciesCount] = useState(0);
    const [rulesSynced, setRulesSynced] = useState(true);
    const [policyConfiguration, setPolicyConfiguration] = useState({
        ConfiguredTokens: [],
        RelyingParties: {},
        RelyingPartiesPingFed: {},
        RadiusClients: [],
        policiesGroups: [],
        GlobalServices: [],
        OktaPolicyByApplicationEnabled: false,
        OktaApps: [],
        allowDesktopMfa: true,
        defaultAllowedTokenTypes: [],
        DomainControllers: [],
        hiddenRiskIndicators: [],
        SSOApplications: {},
        SSOVendors: {}
    });

    const [nextPolicyGroupId, setNextPolicyGroupId] = useState(-1);
    const [shouldFetchData, setShouldFetchData] = useState(-1);
    const [expandedPolicyId, setExpandedPolicyId] = useState(null);
    const [shouldScrollToHash, setShouldScrollToHash] = useState(location.hash);
    const [expandedPolicies, setExpandedPolicies] = useState([]);
    const [collapseForFilter, setCollapseForFilter] = useState(false);
    const [createFromTemplateId, setCreateFromTemplateId] = useState();
    const [createFromInsightId, setCreateFromInsightId] = useState();
    const isCreateFromTemplate = !!createFromTemplateId;

    // Settings
    const [showImportModal, setShowImportModal] = useState(false);
    const [showRefreshPageModal, setShowRefreshPageModal] = useState(false);
    const [refreshPageStatus, setRefreshPageStatus] = useState('');
    const [refreshPageHeader, setRefreshPageHeader] = useState('');
    const [refreshActionSucceed, setRefreshActionSucceed] = useState(true);
    const [showPoliciesSettingsModal, setShowPoliciesSettingsModal] = useState(false);

    /// Filters
    const [filters, setFilters] = useState({ ...POLICIES_FILTERS });
    const [filteredData, setFilteredData] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [filteredPoliciesIds, setFilteredPoliciesIds] = useState(null);
    const [serverFilteredPoliciesIds, setServerFilteredPoliciesIds] = useState(null);

    // New policy
    const [defaultNewPolicy, setDefaultNewPolicy] = useState({});

    const [policiesList, setPoliciesList] = useState([]);
    const [mfaPriorityOrder, setMfaPriorityOrder] = useState([]);
    const [bridgePriorityOrder, setBridgePriorityOrder] = useState([]);

    const allowPolicies = sortPolicies(
        getPoliciesListByAction(policiesList, POLICY_ACTION_TYPES.ALLOW.value, filteredPoliciesIds),
        POLICY_ACTION_TYPES.ALLOW.orderType
    );
    const denyPolicies = sortPolicies(
        getPoliciesListByAction(policiesList, POLICY_ACTION_TYPES.DENY.value, filteredPoliciesIds),
        POLICY_ACTION_TYPES.DENY.orderType
    );

    const mfaPolicies = sortPolicies(
        getPoliciesListByAction(policiesList, POLICY_ACTION_TYPES.MFA.value, filteredPoliciesIds),
        POLICY_ACTION_TYPES.MFA.orderType,
        mfaPriorityOrder
    );
    const notifyPolicies = sortPolicies(
        getPoliciesListByAction(policiesList, POLICY_ACTION_TYPES.NOTIFY.value, filteredPoliciesIds),
        POLICY_ACTION_TYPES.NOTIFY.orderType
    );
    const bridgePolicies = sortPolicies(
        getPoliciesListByAction(policiesList, POLICY_ACTION_TYPES.BRIDGE.value, filteredPoliciesIds),
        POLICY_ACTION_TYPES.BRIDGE.orderType,
        bridgePriorityOrder
    );

    const newPolicies = sortPolicies(
        policiesList.filter((policy) => policy.id < 0 || isNaN(policy.id)),
        POLICY_ACTION_TYPES.NEW.orderType
    );

    const { setLayoutContent, setLayoutHeader } = useTowerLayout();

    useEffect(() => {
        const policyFromParams = location.state?.policy;
        const fromTemplateId = location.state?.fromTemplateId;
        const fromInsightId = location.state?.fromInsightId;

        if (policyFromParams && !isEmptyObject(defaultNewPolicy)) {
            setCreateFromTemplateId(fromTemplateId);
            setCreateFromInsightId(fromInsightId);
            prepareCreatePolicy({ ...defaultNewPolicy, ...policyFromParams });
        }
    }, [location.state?.policy, defaultNewPolicy]);

    useEffect(() => {
        const isBasicNewPolicy = location.hash === '#create'
        if (isBasicNewPolicy && !isEmptyObject(defaultNewPolicy)) {
            prepareCreatePolicy(defaultNewPolicy);
        }
    }, [location.hash, defaultNewPolicy]);

    useEffect(() => {
        async function reloadRulesState() {
            try {
                const response = await httpGet(`/policy/rulesSynced`);
                setRulesSynced(response.data.RulesSynced);
            } catch (err) {
                console.error(err);
            } finally {
                setTimeout(() => {
                    reloadRulesState();
                }, 30 * 1000);
            }
        }

        setFilteredData(false);
        setShouldFetchData(true);
        reloadRulesState();
    }, []);

    useEffect(() => {
        let groupOptions = [];
        if (policyConfiguration.policiesGroups) {
            groupOptions = policyConfiguration.policiesGroups.map((group) => {
                return { value: group.id, label: group.name };
            });
        }
        if (policyConfiguration.policiesGroups || policyConfiguration.allowDesktopMfa) {
            setFilters((currentFilters) => {
                const protectOptions = currentFilters.protect.filterConfig.options;
                const protectOptionsValues = protectOptions.map((protectOption) => {
                    return protectOption.value[0];
                });
                if (policyConfiguration.allowDesktopMfa && !protectOptionsValues.includes('desktop_enabled')) {
                    protectOptions.push({ value: ['desktop_enabled'], label: 'Windows Logon' });
                }
                return {
                    ...currentFilters,
                    policyGroup: {
                        ...currentFilters.policyGroup,
                        filterConfig: { ...currentFilters.policyGroup.filterConfig, options: groupOptions }
                    },
                    protect: {
                        ...currentFilters.protect,
                        filterConfig: { ...currentFilters.protect.filterConfig, options: protectOptions }
                    }
                };
            });
        }
    }, [policyConfiguration]);

    useEffect(() => {
        async function fetchData() {
            const filterValues = {};
            if (location.hash && location.hash !== '#create' && shouldScrollToHash) {
                filterValues['ruleId'] = location.hash.substring(1);
            }

            try {
                setIsLoadingData(true);
                const response = await httpGet(`/policy/rules`, {
                    [HTTP_OPTIONS.REQ_OPTIONS]: { params: { ...filterValues } }
                });

                const rulesData = response.data;
                const defaultPolicyIndex = rulesData.Rules.findIndex((policy) => policy.id === DEFAULT_POLICY_ID);
                if (defaultPolicyIndex >= 0) {
                    const defaultPolicyObj = rulesData.Rules.splice(defaultPolicyIndex, 1)[0];
                    setDefaultNewPolicy(defaultPolicyObj);
                }
                const newPolicies = policiesList.filter((policy) => policy.id < 0 || isNaN(policy.id));
                const newMfaPriorityOrder = buildPriorityOrder(rulesData.Rules, POLICY_ACTION_TYPES.MFA.value);
                const newBridgePriorityOrder = buildPriorityOrder(rulesData.Rules, POLICY_ACTION_TYPES.BRIDGE.value);
                setMfaPriorityOrder(newMfaPriorityOrder);
                setBridgePriorityOrder(newBridgePriorityOrder);
                setPoliciesList((prevState) => [...prevState, ...rulesData.Rules, ...newPolicies]);
                setPolicyConfiguration(rulesData.PolicyConfiguration);
                setPoliciesCount(!rulesData.Rules.length ? 0 : rulesData.Rules.length);
                setRulesSynced(rulesData.RulesSynced);
                setShowLoader(false);
                if (location.hash) {
                    setExpandedPolicyId(location.hash.substring(1));
                }
            } catch (err) {
                console.log(err);
            }
            setIsLoadingData(false);
        }

        if (shouldFetchData) {
            fetchData();
            setShouldFetchData(false);
        }
    }, [policiesList, shouldFetchData, filters, location, shouldScrollToHash, location.hash]);

    useEffect(() => {
        async function getServerFilteredPolicies(filters) {
            setIsLoadingData(true);
            const values = {};
            for (const filterType in filters) {
                let filterKey = filterType;
                if (filterKey === 'adEntitiesAndLocalAccounts') {
                    filterKey = 'usersAndGroups';
                }
                values[filterKey] = filters[filterType].map((f) => f.value);
            }

            const response = await httpGet(`/policy/filterRules`, {
                [HTTP_OPTIONS.REQ_OPTIONS]: { params: values },
                [HTTP_OPTIONS.HANDLE_ERRORS]: true
            });
            return response.data?.filteredIds;
        }

        async function filteredPolicies(clientFilterValues, serverFilterValues, hasFilters) {
            let filteredPoliciesList = [...policiesList];
            if (Object.keys(serverFilterValues).length > 0) {
                let serverFilteredIds = serverFilteredPoliciesIds;
                if (!serverFilteredIds) {
                    serverFilteredIds = await getServerFilteredPolicies(serverFilterValues);
                    setServerFilteredPoliciesIds(serverFilteredIds);
                }
                serverFilteredIds = serverFilteredIds ? serverFilteredIds : [];
                filteredPoliciesList = filteredPoliciesList.filter((policy) => serverFilteredIds.includes(policy.id));
            }

            if (clientFilterValues.policyName) {
                filteredPoliciesList = filteredPoliciesList.filter((policy) =>
                    clientFilterValues.policyName.some((name) => policy.name.toLowerCase().includes(name.toLowerCase()))
                );
            }
            if (clientFilterValues.recentlyUpdated) {
                filteredPoliciesList = filteredPoliciesList.filter(
                    (policy) => Math.abs(moment(policy.update_date + 'Z').diff(moment.utc().format(), 'days')) < 7
                );
            }
            if (clientFilterValues.onlyActivePolicies) {
                filteredPoliciesList = filteredPoliciesList.filter((policy) => policy.state);
            }
            if (clientFilterValues.protect) {
                let filteredProtocols = [];
                for (const protectProtocols of clientFilterValues.protect) {
                    filteredProtocols = [...filteredProtocols, ...protectProtocols];
                }
                filteredPoliciesList = filteredPoliciesList.filter((policy) => {
                    return filteredProtocols.some((protocol) => policy[protocol]);
                });
            }

            if (clientFilterValues.policyGroup) {
                filteredPoliciesList = filteredPoliciesList.filter((policy) =>
                    clientFilterValues.policyGroup.some(
                        (group) =>
                            policy.groups && policy.groups.some((groupObj) => parseInt(groupObj.id) === parseInt(group))
                    )
                );
            }
            const filteredIds = filteredPoliciesList.map((policy) => policy.id);

            if (hasFilters) {
                setPoliciesCount(filteredIds.length);
            } else {
                setPoliciesCount(policiesList.length);
            }
            setFilteredPoliciesIds(filteredIds);
            setIsLoadingData(false);
        }

        const clientFilterValues = {};
        const serverFilterValues = {};
        // calc filters values
        let hasFilters = false;
        for (const fId in filters) {
            const filter = filters[fId];
            if (Array.isArray(filter.value) ? filter.value.length !== 0 : filter.value) {
                if (fId === adEntitiesAndLocalAccounts.id || fId === resources.id) {
                    serverFilterValues[fId] = filter.value;
                } else {
                    clientFilterValues[fId] = filter.value;
                }
                hasFilters = true;
            }
        }

        if (hasFilters) {
            if (collapseForFilter) {
                const newExpandedPolicies = [...expandedPolicies];
                const newPoliciesList = [...policiesList];

                for (const policyIndex in newPoliciesList) {
                    const policy = newPoliciesList[policyIndex];
                    if (policy.isExpanded && !newExpandedPolicies.includes(policy.id)) {
                        newExpandedPolicies.push(policy.id);
                        newPoliciesList[policyIndex] = { ...newPoliciesList[policyIndex], isExpanded: false };
                    }
                }
                if (newExpandedPolicies.length !== expandedPolicies.length) {
                    setExpandedPolicies(newExpandedPolicies);
                    setPoliciesList(newPoliciesList);
                }
                setCollapseForFilter(false);
            }
        } else if (expandedPolicies.length > 0) {
            const newPoliciesList = [...policiesList];
            for (const policyId of expandedPolicies) {
                const policyIndex = newPoliciesList.findIndex((policy) => policy.id === policyId);
                if (policyIndex >= 0) {
                    newPoliciesList[policyIndex] = { ...newPoliciesList[policyIndex], isExpanded: true };
                }
            }
            setPoliciesList(newPoliciesList);
            setExpandedPolicies([]);
        }
        filteredPolicies(clientFilterValues, serverFilterValues, hasFilters);
        setFilteredData(hasFilters);
    }, [filters, policiesList, serverFilteredPoliciesIds, expandedPolicies, collapseForFilter]);

    const fetchPolicyRowData = useCallback( 
        async (policyIndex, scrollToHash) => {
            const scrollToHashOnLoad = () => {
                if (location.hash && location.hash !== '#create' && shouldScrollToHash) {
                    setShouldScrollToHash(false);
                    const newElement = document.createElement('a');
                    newElement.href = location.hash;
                    setTimeout(() => newElement.click(), 250);
                }
            };
            try {
                const response = await httpGet(`/policy/rules/${policiesList[policyIndex].id}`);
                if (response.data) {
                    const newPoliciesList = [...policiesList];
                    const oldPolicyObj = newPoliciesList.splice(policyIndex, 1)[0];
                    const policyObj = response.data.ruleDetails;
                    policyObj.isExpanded = true;
                    policyObj.isLoaded = true;
                    policyObj.shouldExpand = false;
                    policyObj.isValid = response.data.isValid;
                    policyObj.hits = oldPolicyObj.hits;
                    if (scrollToHash) {
                        newPoliciesList.push(policyObj);
                        setPoliciesList(newPoliciesList);
                        scrollToHashOnLoad();
                    }
                    return policyObj;
                }
            } catch (err) {
                console.log(err);
                return false;
            }
        },
        [policiesList, shouldScrollToHash, location]
    );
    useEffect(() => {
        if (expandedPolicyId) {
            const newPoliciesList = [...policiesList];
            const policyIndex = newPoliciesList.findIndex(
                (policy) => policy.id.toString() === expandedPolicyId.toString()
            );
            if (policyIndex < 0) {
                setExpandedPolicyId(null);
                setShouldScrollToHash(false);
                return;
            }
            const policyObj = newPoliciesList.splice(policyIndex, 1)[0];
            if (policyObj.isLoaded) {
                policyObj.isExpanded = !policyObj.isExpanded;
            } else {
                policyObj.isExpanded = true;
                policyObj.isLoaded = false;
            }

            newPoliciesList.push(policyObj);
            setPoliciesList(newPoliciesList);
            if (!policyObj.isLoaded) {
                fetchPolicyRowData(policyIndex, true);
            } else {
                setShouldScrollToHash(false);
            }
            setExpandedPolicyId(null);
        }
    }, [expandedPolicyId, location, policiesList, shouldScrollToHash, fetchPolicyRowData]);

    const buildPriorityOrder = (policies, actionType) => {
        const priorityOrderObjList = getPoliciesListByAction(policies, actionType);
        const sortedPriorityOrder = priorityOrderObjList
            .sort((first, second) => {
                if (first.priority > second.priority) {
                    return 1;
                }
                return -1;
            })
            .map((priorityOrderObj, priorityIndex) => {
                return { policyId: priorityOrderObj.id, originalPriority: priorityIndex + 1 };
            });

        return sortedPriorityOrder;
    };

    const onHeaderClicked = (policyId) => {
        setExpandedPolicyId(policyId.toString());
    };

    const onEditTemplate = () => {
        history.replace('/Policy');
        setExpandedPolicyId(null);
        setPoliciesList((prevPolicies) => prevPolicies.filter((policy) => policy.id >= 0));
    };

    const handlePolicyReorder = async (isApproved, actionType, currentPriorityOrder, currentPoliciesList) => {
        if (isApproved) {
            const priorityData = [];
            let priorityOrderList = [];
            let priorityOrderFunc = () => {};
            const newPriorityOrderList = [];
            if (actionType === POLICY_ACTION_TYPES.MFA.value) {
                priorityOrderList = currentPriorityOrder || mfaPriorityOrder;
                priorityOrderFunc = setMfaPriorityOrder;
            } else if (actionType === POLICY_ACTION_TYPES.BRIDGE.value) {
                priorityOrderList = currentPriorityOrder || bridgePriorityOrder;
                priorityOrderFunc = setBridgePriorityOrder;
            }
            for (const policyIndex in priorityOrderList) {
                priorityData.push({ id: priorityOrderList[policyIndex].policyId, priority: parseInt(policyIndex) + 1 });
                newPriorityOrderList.push({
                    policyId: priorityOrderList[policyIndex].policyId,
                    originalPriority: parseInt(policyIndex) + 1
                });
            }

            try {
                const response = await httpPost(`/policy/updateRulesPriority`, { priorityData: priorityData });
                if (response.status !== 200) {
                    return false;
                }
            } catch (err) {
                console.error(err);
                return false;
            }

            const newPoliciesList = [...(currentPoliciesList || policiesList)];
            for (const priorityObj of priorityData) {
                const policyIndex = newPoliciesList.findIndex((policy) => policy.id === priorityObj.id);
                if (policyIndex >= 0) {
                    newPoliciesList[policyIndex].priority = priorityObj.priority;
                    newPoliciesList[policyIndex].update_date = moment.utc().format(UPDATE_DATE_FORMAT);
                }
            }
            setPoliciesList(newPoliciesList);
            setRulesSynced(false);
            priorityOrderFunc(newPriorityOrderList);
        } else {
            const sortedPriorityOrder = buildPriorityOrder(policiesList, actionType);
            if (actionType === POLICY_ACTION_TYPES.MFA.value) {
                setMfaPriorityOrder(sortedPriorityOrder);
            } else if (actionType === POLICY_ACTION_TYPES.BRIDGE.value) {
                setBridgePriorityOrder(sortedPriorityOrder);
            }
        }
        return true;
    };

    const updateGroups = (oldGroupIdsToNew, newPoliciesGroups) => {
        const newGroupsIds = newPoliciesGroups.map((newGroup) => newGroup.id);
        const updatedPoliciesGroups = [];
        for (const policyGroup of policyConfiguration.policiesGroups) {
            if (policyGroup.id < 0 && Object.keys(oldGroupIdsToNew).includes(policyGroup.id.toString())) {
                policyGroup.id = oldGroupIdsToNew[policyGroup.id];
            }
            if (newGroupsIds.includes(policyGroup.id)) {
                updatedPoliciesGroups.push(policyGroup);
            }
        }
        setPolicyConfiguration({ ...policyConfiguration, policiesGroups: updatedPoliciesGroups });
    };

    const changePolicyState = async (policyId) => {
        const newPoliciesList = [...policiesList];
        const policyIndex = newPoliciesList.findIndex((policy) => policy.id === policyId);
        if (policyIndex < 0) {
            return false;
        }

        const policy = { ...newPoliciesList[policyIndex] };
        const newState = !policy.state;
        try {
            const response = await httpPost('/policy/updateRulesState', {
                ruleID: policy.id,
                name: policy.name,
                newState: newState
            });
            if (response.status !== 200) {
                return false;
            }
            policy.state = newState;
            policy.update_date = moment.utc().format(UPDATE_DATE_FORMAT);
            newPoliciesList.splice(policyIndex, 1);
            newPoliciesList.push(policy);
            setPoliciesList(newPoliciesList);

            setRulesSynced(false);
            return policy;
        } catch (err) {
            console.error(err);
            return false;
        }
    };

    const handleStartFromTemplateClicked = () => {
        history.push('/Policy/templates');
    };

    const prepareCreatePolicy = (newPolicyData = defaultNewPolicy) => {
        if (newPolicies.length > 0) {
            return;
        }
        const newPoliciesList = [...policiesList];
        const newPolicyObj = { ...newPolicyData };
        newPoliciesList.push({ ...newPolicyObj, isLoaded: true, isValid: true });
        setExpandedPolicyId(newPolicyObj.id);
        setPoliciesList(newPoliciesList);
        const newElement = document.createElement('a');
        newElement.href = `#${newPolicyObj.id}`;
        setTimeout(() => newElement.click(), 250);

        setDefaultNewPolicy({ ...defaultNewPolicy, id: newPolicyObj.id - 1 });
    };

    const handleAddPolicyClicked = () => {
        prepareCreatePolicy();
    };

    const handleDeletePolicy = async (policyId) => {
        const newPoliciesList = [...policiesList];
        const policyIndex = newPoliciesList.findIndex((policy) => policy.id === policyId);

        if (policyIndex < 0) {
            return false;
        }

        if (policyId >= 0) {
            try {
                const response = await secureAxios.delete(`/policy/rules/${policyId}`, {
                    data: { state: newPoliciesList[policyIndex].state, name: newPoliciesList[policyIndex].name }
                });
                if (response.data.redirect) {
                    // data.redirect contains the string URL to redirect to
                    window.location.href = response.data.redirect;
                    return;
                }
                if (response.status !== 200) {
                    return false;
                }
                updateGroups({}, response.data.policiesGroups);
            } catch (err) {
                console.error(err);
                return false;
            }

            setRulesSynced(false);
            setPoliciesCount(policiesCount - 1);
        }

        newPoliciesList.splice(policyIndex, 1);
        setPoliciesList(newPoliciesList);

        if (mfaPriorityOrder.some((p) => p.policyId === policyId) > 0) {
            const newPriorityOrder = [...mfaPriorityOrder];
            const priorityIndex = newPriorityOrder.findIndex((priorityObj) => priorityObj.policyId === policyId);
            if (priorityIndex >= 0) {
                newPriorityOrder.splice(priorityIndex, 1);
                handlePolicyReorder(true, POLICY_ACTION_TYPES.MFA.value, newPriorityOrder, newPoliciesList);
            }
        } else if (bridgePriorityOrder.some((p) => p.policyId === policyId) > 0) {
            const newPriorityOrder = [...bridgePriorityOrder];
            const priorityIndex = newPriorityOrder.findIndex((priorityObj) => priorityObj.policyId === policyId);
            if (priorityIndex >= 0) {
                newPriorityOrder.splice(priorityIndex, 1);
                handlePolicyReorder(true, POLICY_ACTION_TYPES.BRIDGE.value, newPriorityOrder, newPoliciesList);
            }
        }

        return true;
    };

    const handleSavePolicy = async (policyObj) => {
        const newPoliciesList = [...policiesList];
        const policyIndex = newPoliciesList.findIndex((policy) => policy.id === policyObj.id);
        let shouldSetMfaPriorityToList = false;
        let shouldSetBridgePriorityToList = false;
        if (policyIndex < 0) {
            return { result: false };
        }
        const prevPolicyObj = newPoliciesList[policyIndex];
        const isNewPolicy = policyObj.id < 0 || isNaN(policyObj.id);
        if (
            policyObj.action_type !== POLICY_ACTION_TYPES.MFA.value &&
            policyObj.action_type !== POLICY_ACTION_TYPES.BRIDGE.value
        ) {
            policyObj.unpaired_users_action = UNPAIRED_USERS_ACTION_OPTIONS.DEFAULT.value;
            policyObj.priority = 0;
        } else if (
            policyObj.action_type === POLICY_ACTION_TYPES.MFA.value &&
            (prevPolicyObj.action_type !== POLICY_ACTION_TYPES.MFA.value || isNewPolicy)
        ) {
            policyObj.priority = mfaPriorityOrder.length + 1;
            shouldSetMfaPriorityToList = true;
            if (prevPolicyObj.action_type === POLICY_ACTION_TYPES.BRIDGE.value && !isNewPolicy) {
                shouldSetBridgePriorityToList = true;
            }
        } else if (
            policyObj.action_type === POLICY_ACTION_TYPES.BRIDGE.value &&
            (prevPolicyObj.action_type !== POLICY_ACTION_TYPES.BRIDGE.value || isNewPolicy)
        ) {
            policyObj.priority = bridgePriorityOrder.length + 1;
            shouldSetBridgePriorityToList = true;
            if (prevPolicyObj.action_type === POLICY_ACTION_TYPES.MFA.value && !isNewPolicy) {
                shouldSetMfaPriorityToList = true;
            }
        } else {
            policyObj.priority = prevPolicyObj.priority;
        }
        if (!isNewPolicy) {
            policyObj.state = prevPolicyObj.state;
        }

        if (!policyObj.desktop_enabled || policyObj.risk_enabled) {
            policyObj.mode_type = POLICY_MODE_TYPES.ONLINE.value;
        }

        if (!policyObj.kerberos_enabled) {
            policyObj.unmanaged_hosts = [];
        }

        const options = {
            data: { ...policyObj }
        };
        if (createFromTemplateId) {
            options.data.fromTemplateId = createFromTemplateId;
            options.data.fromInsightId = createFromInsightId;
        }
        options.method = isNewPolicy ? 'post' : 'put';
        options.url = isNewPolicy ? '/policy/rules' : `/policy/rules/${policyObj.id}`;

        try {
            const response = await secureAxios(options);
            if (response.data.redirect) {
                // data.redirect contains the string URL to redirect to
                window.location = response.data.redirect;
                return { result: false };
            }
            if (response.status !== 200) {
                return { result: false };
            }

            updateGroups(response.data.oldGroupIdsToNew, response.data.policiesGroups);

            if (response.data.ruleID) {
                policyObj.id = response.data.ruleID;
            }

            if (shouldSetMfaPriorityToList) {
                const newMfaPriorityOrder = [...mfaPriorityOrder];
                newMfaPriorityOrder.push({ policyId: policyObj.id, originalPriority: newMfaPriorityOrder.length + 1 });
                setMfaPriorityOrder(newMfaPriorityOrder);
            }
            if (shouldSetBridgePriorityToList) {
                const newBridgePriorityOrder = [...bridgePriorityOrder];
                newBridgePriorityOrder.push({
                    policyId: policyObj.id,
                    originalPriority: newBridgePriorityOrder.length + 1
                });
                setBridgePriorityOrder(newBridgePriorityOrder);
            }

            policyObj.update_date = moment.utc().format(UPDATE_DATE_FORMAT);

            let updateServerFilteredPoliciesIds = false;
            if (
                (policyObj.entities &&
                    prevPolicyObj.entities &&
                    policyObj.entities.length !== prevPolicyObj.entities.length) ||
                (policyObj.servers &&
                    prevPolicyObj.servers &&
                    policyObj.servers.length !== prevPolicyObj.servers.length)
            ) {
                updateServerFilteredPoliciesIds = true;
            } else {
                const selectedEntitiesGuids = (policyObj.entities || []).map((user) => user.guid);
                for (const entity of prevPolicyObj.entities || []) {
                    if (!selectedEntitiesGuids.includes(entity.guid)) {
                        updateServerFilteredPoliciesIds = true;
                        break;
                    }
                }
                if (!updateServerFilteredPoliciesIds) {
                    const selectedServersGuids = (policyObj.servers || []).map((user) => user.guid);
                    for (const server of prevPolicyObj.servers || []) {
                        if (!selectedServersGuids.includes(server.guid)) {
                            updateServerFilteredPoliciesIds = true;
                            break;
                        }
                    }
                }
            }
            if (updateServerFilteredPoliciesIds) {
                setServerFilteredPoliciesIds(null);
            }

            newPoliciesList.splice(policyIndex, 1);
            newPoliciesList.push(policyObj);
            setPoliciesList(newPoliciesList);
            setPoliciesCount(newPoliciesList.length);
            setRulesSynced(false);
            const newElement = document.createElement('a');
            newElement.href = `#${policyObj.id}`;
            setTimeout(() => newElement.click(), 250);
            setCreateFromTemplateId(undefined);
            setCreateFromInsightId(undefined);
            return { result: policyObj.id };
        } catch (err) {
            console.error(err);
            let errorDesc = '';
            if (err.response) {
                const { data } = err.response;
                if (data.errorDesc) {
                    errorDesc = data.errorDesc;
                }
            }
            return { result: false, errorDesc };
        }
    };

    const importApproveHandler = async (importFileContent) => {
        setShowImportModal(false);
        const reqData = JSON.parse(importFileContent);

        const result = await httpPost('/policy/importRules', reqData, { [HTTP_OPTIONS.HANDLE_ERRORS]: true });

        if (result.data) {
            setShowRefreshPageModal(true);
            setRefreshPageStatus(result.data.userMsg);
            setRefreshActionSucceed(result.data.result);
            setRefreshPageHeader('Import policies status');
        }
    };

    const exportPolicies = async () => {
        if (policiesCount === 0) {
            return;
        }
        try {
            const results = await httpGet('/policy/exportRules/');
            if (!results.data.rules) results.data.rules = [];
            const policiesString = JSON.stringify({
                environmentId: results.data.environmentId,
                rules: results.data.rules
            });
            const blob = new Blob([policiesString], { type: 'application/json' });
            fileSaver.saveAs(blob, `SilverfortPolicies_${moment().local().format()}.json`);
        } catch (err) {
            console.error(err);
        }
    };

    const exportPoliciesToCsv = async () => {
        if (policiesCount === 0) {
            return;
        }
        try {
            const response = await httpGet(`/policy/exportRulesToCsv`);
            const downloadData = response.data;
            if (downloadData.responseID) {
                downloadFile(`/policy/downloadRulesCsv/${downloadData.responseID}`);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const filterValueChangedHandler = (filterId, newValue, isExcluded = false) => {
        const newFilters = { ...filters };
        newFilters[filterId] = { ...newFilters[filterId] };

        if (newFilters[filterId].filterType === EXTERNAL) {
            const newEntities = newValue.objectEntities || [];
            newFilters[filterId].value = newEntities.map((entity) => {
                return { label: entity.name || entity.display_name, value: entity.guid };
            });
            newFilters[filterId].externalSelections = { entities: newValue.objectEntities };
        } else {
            newFilters[filterId].value = newValue !== null ? newValue : newFilters[filterId].defaultValue;
        }

        if (filterId === adEntitiesAndLocalAccounts.id || filterId === resources.id) {
            setServerFilteredPoliciesIds(null);
        }

        newFilters[filterId].isExcluded = isExcluded;

        setCollapseForFilter(true);
        setFilters(newFilters);
    };

    const savePoliciesSettings = async (newSettings) => {
        const response = await httpPost(`/policy/saveSettings`, { newSettings: newSettings });
        if (response.status !== 200 || response.data?.result === false) {
            setRefreshActionSucceed(false);
            return false;
        }
        setShowPoliciesSettingsModal(false);
        setShowRefreshPageModal(true);
        setRefreshActionSucceed(true);
        setRefreshPageStatus('Save settings completed successfully');
        setRefreshPageHeader('Save settings status');
        return true;
    };

    const duplicatePolicy = async (policyObj) => {
        const newPoliciesList = [...policiesList];
        let duplicatePolicyObj = { ...policyObj };

        if (!policyObj.isLoaded) {
            const policyIndex = newPoliciesList.findIndex((policy) => policy.id.toString() === policyObj.id.toString());
            if (policyIndex < 0) {
                return;
            }
            const loadedPolicyObj = await fetchPolicyRowData(policyIndex, false);
            if (!loadedPolicyObj) {
                return;
            }
            const newPolicyObj = { ...loadedPolicyObj, isExpanded: false };
            newPoliciesList.splice(policyIndex, 1);
            newPoliciesList.push(newPolicyObj);
            duplicatePolicyObj = { ...loadedPolicyObj };
        }
        duplicatePolicyObj.id = defaultNewPolicy.id;
        duplicatePolicyObj.name = `${policyObj.name} (Duplicate)`;
        duplicatePolicyObj.hits = 0;
        duplicatePolicyObj.state = false;

        newPoliciesList.push({ ...duplicatePolicyObj, isLoaded: true, isValid: true, isExpanded: true });
        setPoliciesList(newPoliciesList);
        const newElement = document.createElement('a');
        newElement.href = `#${duplicatePolicyObj.id}`;
        setTimeout(() => newElement.click(), 250);
        setDefaultNewPolicy({ ...defaultNewPolicy, id: duplicatePolicyObj.id - 1 });
    };

    const bannerData = (
        <span className={`${navBarStyles.navbarRightSide} w-fit`}>
            {(filteredData || policiesList.length !== 0) && (
                <>
                    {systemMode !== SYSTEM_MODES.SAAS && (
                        <div className="flex items-center gap-2">
                            <span className={rulesSynced ? '' : 'animate-spin'}>
                                <Icon sie={20} icon={Icons.Sync} />
                            </span>
                            <div className={'text-13 text-sf-gray-500 font-sf pr-4'}>
                                {rulesSynced ? 'All Policies are Synced' : 'Policy Sync is in Progress'}
                            </div>
                        </div>
                    )}
                    <div className="flex gap-2">
                        <Button
                            text="Start From Template"
                            onClick={handleStartFromTemplateClicked}
                            disabled={!isEditor}
                        />

                        <Button
                            iconLeft={Icons.Add}
                            text="Create Policy"
                            onClick={handleAddPolicyClicked}
                            disabled={!isEditor}
                        />
                    </div>
                </>
            )}
            <DropdownButton
                id={navBarStyles.navbarOptionsDropdown}
                pullRight
                noCaret
                title={<Icon size={20} icon={Icons.SettingsPage} />}>
                <MenuItem
                    disabled={!isEditor}
                    onClick={() => {
                        setShowImportModal(isEditor);
                    }}>
                    <i className={`fa fa-upload ${styles.policiesSettingsOptionIcon}`}></i>
                    Import policies
                </MenuItem>
                <MenuItem onClick={exportPolicies} disabled={policiesCount === 0}>
                    <i className={`fa fa-download ${styles.policiesSettingsOptionIcon}`}></i>
                    Export policies (JSON)
                </MenuItem>
                <MenuItem onClick={exportPoliciesToCsv} disabled={policiesCount === 0}>
                    <i className={`fa fa-download ${styles.policiesSettingsOptionIcon}`}></i>
                    Export policies (CSV)
                </MenuItem>
                <MenuItem
                    disabled={!isEditor}
                    onClick={() => {
                        setShowPoliciesSettingsModal(isEditor);
                    }}>
                    <i className={`fa fa-cogs ${styles.policiesSettingsOptionIcon}`}></i>
                    Configure policies
                </MenuItem>
            </DropdownButton>
        </span>
    );

    useEffect(() => {
        setLayoutHeader(`Policies (${policiesCount})`);
        setLayoutContent(bannerData);
        return () => {
            setLayoutHeader('');
            setLayoutContent('');
        };
    }, [setLayoutContent, setLayoutHeader, policiesList, rulesSynced, isEditor, policiesCount]);

    return (
        <div id={styles.policiesPageContainer} className="z-50">
            {showLoader && (
                <div className="silverfort-loader">
                    <div className="logo"></div>
                </div>
            )}
            <div className={styles.pageWrapper}>
                {policiesCount === 0 && newPolicies.length === 0 && !filteredData && !isLoadingData && (
                    <div className={styles.pageContent}>
                        <div className={styles.noPolicies}>
                            <div className={styles.imgCircle}>
                                <img className={styles.policyImg} src={whitePoliciesSelectedIcon} alt="" />
                            </div>
                            <div className={styles.noPoliciesMsg}>No policies at the moment</div>
                            <Button
                                iconLeft={Icons.Add}
                                text="Create Policy"
                                onClick={handleAddPolicyClicked}
                                disabled={!isEditor}
                            />
                        </div>
                    </div>
                )}
                {(policiesCount !== 0 || newPolicies.length !== 0 || filteredData) && (
                    <div className={styles.pageContent}>
                        <div className={styles.contentHeader}>
                            <FiltersMenu filters={Object.values(filters)} onValueChange={filterValueChangedHandler} />
                        </div>
                        <div className={styles.contentBody}>
                            <Collapse in={newPolicies.length !== 0}>
                                <PoliciesSection
                                    key="PoliciesSection_new"
                                    action={POLICY_ACTION_TYPES.NEW}
                                    isEditor={isEditor}
                                    policiesData={newPolicies}
                                    isNewPolicies={true}
                                    handleDeletePolicy={handleDeletePolicy}
                                    handleSavePolicy={handleSavePolicy}
                                    policyConfiguration={policyConfiguration}
                                    setPolicyConfiguration={setPolicyConfiguration}
                                    onHeaderClicked={onHeaderClicked}
                                    nextPolicyGroupId={nextPolicyGroupId}
                                    setNextPolicyGroupId={setNextPolicyGroupId}
                                    isCreateFromTemplate={isCreateFromTemplate}
                                    templateToEdit={templateToEdit}
                                    onEditTemplate={onEditTemplate}
                                />
                            </Collapse>
                            <PoliciesSection
                                key="PoliciesSection_allow"
                                action={POLICY_ACTION_TYPES.ALLOW}
                                isEditor={isEditor}
                                policiesData={allowPolicies}
                                changePolicyState={changePolicyState}
                                handleDeletePolicy={handleDeletePolicy}
                                handleSavePolicy={handleSavePolicy}
                                policyConfiguration={policyConfiguration}
                                setPolicyConfiguration={setPolicyConfiguration}
                                filteredData={filteredData}
                                onHeaderClicked={onHeaderClicked}
                                nextPolicyGroupId={nextPolicyGroupId}
                                setNextPolicyGroupId={setNextPolicyGroupId}
                                duplicatePolicy={duplicatePolicy}
                                isCreateFromTemplate={isCreateFromTemplate}
                            />
                            <PoliciesSection
                                key="PoliciesSection_deny"
                                action={POLICY_ACTION_TYPES.DENY}
                                isEditor={isEditor}
                                policiesData={denyPolicies}
                                changePolicyState={changePolicyState}
                                handleDeletePolicy={handleDeletePolicy}
                                handleSavePolicy={handleSavePolicy}
                                policyConfiguration={policyConfiguration}
                                setPolicyConfiguration={setPolicyConfiguration}
                                filteredData={filteredData}
                                onHeaderClicked={onHeaderClicked}
                                nextPolicyGroupId={nextPolicyGroupId}
                                setNextPolicyGroupId={setNextPolicyGroupId}
                                duplicatePolicy={duplicatePolicy}
                                isCreateFromTemplate={isCreateFromTemplate}
                            />

                            <PoliciesSection
                                key="PoliciesSection_bridge"
                                action={POLICY_ACTION_TYPES.BRIDGE}
                                isEditor={isEditor}
                                policiesData={bridgePolicies}
                                handleReorder={(isApproved) =>
                                    handlePolicyReorder(isApproved, POLICY_ACTION_TYPES.BRIDGE.value)
                                }
                                changePolicyState={changePolicyState}
                                handleDeletePolicy={handleDeletePolicy}
                                handleSavePolicy={handleSavePolicy}
                                policyConfiguration={policyConfiguration}
                                setPolicyConfiguration={setPolicyConfiguration}
                                filteredData={filteredData}
                                onHeaderClicked={onHeaderClicked}
                                priorityOrder={bridgePriorityOrder}
                                setPriorityOrder={setBridgePriorityOrder}
                                nextPolicyGroupId={nextPolicyGroupId}
                                setNextPolicyGroupId={setNextPolicyGroupId}
                                duplicatePolicy={duplicatePolicy}
                                isCreateFromTemplate={isCreateFromTemplate}
                            />

                            <PoliciesSection
                                key="PoliciesSection_mfa"
                                action={POLICY_ACTION_TYPES.MFA}
                                isEditor={isEditor}
                                policiesData={mfaPolicies}
                                handleReorder={(isApproved) =>
                                    handlePolicyReorder(isApproved, POLICY_ACTION_TYPES.MFA.value)
                                }
                                changePolicyState={changePolicyState}
                                handleDeletePolicy={handleDeletePolicy}
                                handleSavePolicy={handleSavePolicy}
                                policyConfiguration={policyConfiguration}
                                setPolicyConfiguration={setPolicyConfiguration}
                                filteredData={filteredData}
                                onHeaderClicked={onHeaderClicked}
                                priorityOrder={mfaPriorityOrder}
                                setPriorityOrder={setMfaPriorityOrder}
                                nextPolicyGroupId={nextPolicyGroupId}
                                setNextPolicyGroupId={setNextPolicyGroupId}
                                duplicatePolicy={duplicatePolicy}
                                isCreateFromTemplate={isCreateFromTemplate}
                            />
                            <PoliciesSection
                                key="PoliciesSection_notify"
                                action={POLICY_ACTION_TYPES.NOTIFY}
                                isEditor={isEditor}
                                policiesData={notifyPolicies}
                                changePolicyState={changePolicyState}
                                handleDeletePolicy={handleDeletePolicy}
                                handleSavePolicy={handleSavePolicy}
                                policyConfiguration={policyConfiguration}
                                setPolicyConfiguration={setPolicyConfiguration}
                                filteredData={filteredData}
                                onHeaderClicked={onHeaderClicked}
                                nextPolicyGroupId={nextPolicyGroupId}
                                setNextPolicyGroupId={setNextPolicyGroupId}
                                duplicatePolicy={duplicatePolicy}
                                isCreateFromTemplate={isCreateFromTemplate}
                            />
                        </div>
                    </div>
                )}
                {showImportModal && (
                    <ImportFromFile
                        cancelClicked={() => {
                            setShowImportModal(false);
                        }}
                        importClicked={importApproveHandler}
                        modalTitle={'Policies'}
                        importDescName={'policies'}
                        maxFileSize={10000000}
                        maxFileSizeText={'10 MB'}
                        importType={'JSON'}
                    />
                )}
                {showRefreshPageModal && (
                    <ConfirmationMessage
                        title={refreshPageHeader}
                        body={
                            <div>
                                {refreshPageStatus + '.'}
                                {refreshActionSucceed && <span>(you need to reload the page to see changes.)</span>}
                            </div>
                        }
                        approveText={refreshActionSucceed ? 'Reload page now' : 'Close'}
                        cancelText={'Reload later'}
                        hideCancelButton={!refreshActionSucceed}
                        OnCancel={() => {
                            setRefreshPageStatus('');
                            setShowRefreshPageModal(false);
                        }}
                        OnApprove={() => {
                            if (refreshActionSucceed) {
                                window.location.reload(true);
                            } else {
                                setRefreshPageStatus('');
                                setShowRefreshPageModal(false);
                            }
                        }}
                        disableCloseButton={true}
                    />
                )}
                {showPoliciesSettingsModal && (
                    <PoliciesSettingsModal
                        policyConfiguration={policyConfiguration}
                        onCancel={() => {
                            setShowPoliciesSettingsModal(false);
                        }}
                        onApprove={savePoliciesSettings}
                        defaultPolicy={newPolicies.length > 0 ? newPolicies[0] : {}}
                    />
                )}
            </div>
        </div>
    );
};

export default Policies;
